'use client';

import Image from 'next/image';
import type { ReactNode } from 'react';

interface AuthShellProps {
  children: ReactNode;
  header?: ReactNode;
  contentClassName?: string;
  contentAlignment?: 'start' | 'center';
}

export function AuthShell({
  children,
  header,
  contentClassName = 'max-w-sm',
  contentAlignment,
}: AuthShellProps) {
  return (
    <div className="flex min-h-screen bg-white">
      <div className="flex w-full flex-col lg:w-[45%]">
        {header && (
          <div
            className={
              contentAlignment === 'center'
                ? 'bg-white pt-6 sm:pt-10'
                : 'border-b border-neutral-100 bg-white py-4 shadow-[0px_2px_8px_0px_rgba(24,29,39,0.05)]'
            }
          >
            <div
              className={
                contentAlignment === 'center'
                  ? 'px-4 sm:px-6 lg:px-8 xl:px-12'
                  : `mx-auto w-full px-4 sm:px-6 lg:px-8 xl:px-12 ${contentClassName}`
              }
            >
              <div
                className={
                  contentAlignment === 'center' ? `mx-auto w-full ${contentClassName}` : undefined
                }
              >
                {header}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-1 flex-col items-center px-4 py-8 sm:px-6 lg:px-8 xl:px-12">
          <div className={`flex w-full flex-1 flex-col ${contentClassName}`}>
            <div
              className={
                contentAlignment === 'center'
                  ? 'flex flex-1 flex-col justify-center py-10 sm:py-12'
                  : header
                    ? 'flex flex-1 flex-col pt-10'
                    : 'flex flex-1 flex-col justify-center py-12'
              }
            >
              {children}
            </div>

            <div className="mt-auto pb-4">
              <div className="border-brand-100 flex flex-col gap-1 border-l-2 pl-4">
                <p className="text-[10px] font-medium tracking-wider text-neutral-400 uppercase">
                  © {new Date().getFullYear()} Arbitralis. Todos os direitos reservados.
                </p>
                <p className="text-[10px] text-neutral-400">Empresa de tecnologia jurídica</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative hidden w-[55%] lg:flex">
        <div className="bg-brand-900 absolute inset-0 overflow-hidden">
          <Image
            src="/backhome.webp"
            alt="Background"
            fill
            className="object-cover"
            priority
            sizes="55vw"
          />
          <div className="from-brand-900/80 to-brand-900/30 absolute inset-0 bg-gradient-to-t via-transparent" />
        </div>

        <div className="relative z-10 flex w-full flex-col">
          <div className="flex-1" />
          <div className="w-full p-12 pb-24">
            <div className="grid w-full grid-cols-2 gap-12">
              <div className="space-y-4 text-left">
                <div className="h-0.5 w-12 bg-white/40" />
                <h3 className="text-xs font-bold tracking-[0.2em] text-white uppercase">
                  Segurança Jurídica
                </h3>
                <p className="text-sm leading-relaxed text-white/70">
                  Procedimentos pautados pelo rigor técnico e legal, garantindo plena validade e
                  segurança às decisões institucionais.
                </p>
              </div>

              <div className="space-y-4 text-left">
                <div className="h-0.5 w-12 bg-white/40" />
                <h3 className="text-xs font-bold tracking-[0.2em] text-white uppercase">
                  Método e Confiança
                </h3>
                <p className="text-sm leading-relaxed text-white/70">
                  Excelência na resolução de conflitos, unindo agilidade processual com a solidez de
                  um corpo técnico especializado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
